import { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { serviceRequestService } from '../../../services/serviceRequestService';

const severityOptions = [
  { value: 'emergency', label: 'Emergency (active flooding or major damage risk)' },
  { value: 'urgent', label: 'Urgent (same-day service needed)' },
  { value: 'normal', label: 'Normal (within 24 hours)' },
  { value: 'flexible', label: 'Flexible (this week)' }
];

const locationOptions = [
  { value: 'kitchen', label: 'Kitchen' },
  { value: 'bathroom', label: 'Bathroom' },
  { value: 'laundry', label: 'Laundry' },
  { value: 'basement', label: 'Basement' },
  { value: 'outside', label: 'Outside / Yard' },
  { value: 'multiple', label: 'Multiple Areas' }
];

const issueTypeOptions = [
  { value: 'leak', label: 'Leak / Drip' },
  { value: 'blockage', label: 'Blocked Drain / Toilet' },
  { value: 'low-pressure', label: 'Low Water Pressure' },
  { value: 'no-water', label: 'No Water Supply' },
  { value: 'geyser', label: 'Geyser / Water Heater Issue' },
  { value: 'burst-pipe', label: 'Burst Pipe' },
  { value: 'sewer-smell', label: 'Sewer Smell / Odor' },
  { value: 'noise', label: 'Pipe Noise / Hammering' },
  { value: 'other', label: 'Other' }
];

const propertyOptions = [
  { value: 'house', label: 'House' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'office', label: 'Office / Commercial' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'other', label: 'Other' }
];

const preferredTimeOptions = [
  { value: 'morning', label: 'Morning (8AM - 12PM)' },
  { value: 'afternoon', label: 'Afternoon (12PM - 5PM)' },
  { value: 'evening', label: 'Evening (5PM - 8PM)' },
  { value: 'anytime', label: 'Anytime' }
];

const hazardOptions = [
  { id: 'electrical-risk', label: 'Water near electrical points' },
  { id: 'slip-risk', label: 'Slip/fall hazard' },
  { id: 'sewage', label: 'Sewage backup / contamination' },
  { id: 'structural', label: 'Wall/ceiling/floor damage' }
];

const makeDefaultForm = () => ({
  name: '',
  phone: '',
  email: '',
  urgency: '',
  preferredTime: '',
  location: '',
  issueType: '',
  propertyType: '',
  startTime: '',
  detailedDescription: '',
  hazards: []
});

const ProblemDiagnosisTool = ({ onEmergencyClick }) => {
  const [formData, setFormData] = useState(makeDefaultForm());
  const [photoFiles, setPhotoFiles] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [voiceFile, setVoiceFile] = useState(null);
  const [voicePreviewUrl, setVoicePreviewUrl] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [showCameraCapture, setShowCameraCapture] = useState(false);
  const [cameraError, setCameraError] = useState('');
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const voiceChunksRef = useRef([]);
  const cameraVideoRef = useRef(null);
  const cameraStreamRef = useRef(null);
  const cameraInputRef = useRef(null);
  const uploadInputRef = useRef(null);

  const recordingSupported = useMemo(() => {
    return Boolean(
      typeof window !== 'undefined' &&
      window?.MediaRecorder &&
      navigator?.mediaDevices?.getUserMedia
    );
  }, []);

  useEffect(() => {
    return () => {
      photoPreviews?.forEach((preview) => URL.revokeObjectURL(preview));
      if (voicePreviewUrl) {
        URL.revokeObjectURL(voicePreviewUrl);
      }
      if (mediaStreamRef?.current) {
        mediaStreamRef?.current?.getTracks()?.forEach((track) => track?.stop());
      }
      if (cameraStreamRef?.current) {
        cameraStreamRef?.current?.getTracks()?.forEach((track) => track?.stop());
      }
    };
  }, [photoPreviews, voicePreviewUrl]);

  const stopCameraCapture = () => {
    if (cameraStreamRef?.current) {
      cameraStreamRef?.current?.getTracks()?.forEach((track) => track?.stop());
      cameraStreamRef.current = null;
    }
    if (cameraVideoRef?.current) {
      cameraVideoRef.current.srcObject = null;
    }
    setShowCameraCapture(false);
  };

  const appendPhotos = (selectedFiles) => {
    if (!selectedFiles?.length) return;

    const mergedFiles = [...photoFiles, ...selectedFiles]?.slice(0, 5);
    const oversized = mergedFiles?.find((file) => file?.size > 10 * 1024 * 1024);
    if (oversized) {
      setSubmitError('Each photo must be 10MB or smaller.');
      return;
    }

    setSubmitError('');
    photoPreviews?.forEach((preview) => URL.revokeObjectURL(preview));
    const previews = mergedFiles?.map((file) => URL.createObjectURL(file));
    setPhotoFiles(mergedFiles);
    setPhotoPreviews(previews);
  };

  const startCameraCapture = async () => {
    setCameraError('');
    setSubmitError('');

    try {
      const stream = await navigator?.mediaDevices?.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false
      });

      cameraStreamRef.current = stream;
      setShowCameraCapture(true);

      setTimeout(() => {
        if (cameraVideoRef?.current) {
          cameraVideoRef.current.srcObject = stream;
          cameraVideoRef.current.play()?.catch(() => {});
        }
      }, 0);
    } catch (error) {
      setCameraError(error?.message || 'Unable to access camera on this device/browser.');
    }
  };

  const capturePhotoFromCamera = () => {
    const video = cameraVideoRef?.current;
    if (!video || !video?.videoWidth || !video?.videoHeight) {
      setCameraError('Camera is not ready yet. Please try again.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video?.videoWidth;
    canvas.height = video?.videoHeight;
    const context = canvas.getContext('2d');
    context?.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob((blob) => {
      if (!blob) {
        setCameraError('Could not capture image. Please try again.');
        return;
      }

      const file = new File([blob], `camera-photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
      appendPhotos([file]);
      stopCameraCapture();
    }, 'image/jpeg', 0.92);
  };

  const handleFieldChange = (event) => {
    const { name, value } = event?.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const toggleHazard = (hazardId) => {
    setFormData((prev) => {
      const alreadySelected = prev?.hazards?.includes(hazardId);
      const hazards = alreadySelected
        ? prev?.hazards?.filter((hazard) => hazard !== hazardId)
        : [...prev?.hazards, hazardId];
      return { ...prev, hazards };
    });
  };

  const handlePhotoChange = (event) => {
    const selected = Array.from(event?.target?.files || []);
    if (!selected?.length) return;
    appendPhotos(selected);

    if (event?.target) {
      event.target.value = '';
    }
  };

  const clearPhotos = () => {
    photoPreviews?.forEach((preview) => URL.revokeObjectURL(preview));
    setPhotoPreviews([]);
    setPhotoFiles([]);
  };

  const startVoiceRecording = async () => {
    setSubmitError('');

    try {
      const stream = await navigator?.mediaDevices?.getUserMedia({ audio: true });
      mediaStreamRef.current = stream;

      const recorder = new MediaRecorder(stream);
      voiceChunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event?.data?.size > 0) {
          voiceChunksRef.current?.push(event?.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(voiceChunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], `voice-note-${Date.now()}.webm`, { type: blob?.type });

        if (voicePreviewUrl) {
          URL.revokeObjectURL(voicePreviewUrl);
        }

        const objectUrl = URL.createObjectURL(blob);
        setVoicePreviewUrl(objectUrl);
        setVoiceFile(file);

        if (mediaStreamRef?.current) {
          mediaStreamRef?.current?.getTracks()?.forEach((track) => track?.stop());
          mediaStreamRef.current = null;
        }
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (error) {
      setSubmitError(error?.message || 'Unable to access microphone for voice recording.');
    }
  };

  const stopVoiceRecording = () => {
    if (mediaRecorderRef?.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleVoiceUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    if (!file?.type?.startsWith('audio/')) {
      setSubmitError('Please upload a valid audio file.');
      return;
    }

    if (file?.size > 15 * 1024 * 1024) {
      setSubmitError('Voice note must be 15MB or smaller.');
      return;
    }

    if (voicePreviewUrl) {
      URL.revokeObjectURL(voicePreviewUrl);
    }

    setSubmitError('');
    setVoiceFile(file);
    setVoicePreviewUrl(URL.createObjectURL(file));
  };

  const clearVoiceNote = () => {
    if (voicePreviewUrl) {
      URL.revokeObjectURL(voicePreviewUrl);
    }
    setVoicePreviewUrl('');
    setVoiceFile(null);
  };

  const validate = () => {
    const nextErrors = {};

    if (!formData?.name?.trim()) {
      nextErrors.name = 'Name is required';
    }

    if (!formData?.phone?.trim()) {
      nextErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10,15}$/?.test(formData?.phone?.replace(/\s|\+|-/g, ''))) {
      nextErrors.phone = 'Please enter a valid phone number';
    }

    if (formData?.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      nextErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.urgency) nextErrors.urgency = 'Urgency is required';
    if (!formData?.location) nextErrors.location = 'Location is required';
    if (!formData?.issueType) nextErrors.issueType = 'Issue type is required';
    return nextErrors;
  };

  const toLabel = (options, value) => options?.find((opt) => opt?.value === value)?.label || value || 'Not provided';

  const buildDiagnosticPayload = () => ({
    tool: 'plumbing-problem-diagnosis-v2',
    submittedAt: new Date()?.toISOString(),
    reporter: {
      name: formData?.name,
      phone: formData?.phone,
      email: formData?.email || null
    },
    severity: {
      urgency: formData?.urgency,
      urgencyLabel: toLabel(severityOptions, formData?.urgency)
    },
    issue: {
      location: formData?.location,
      locationLabel: toLabel(locationOptions, formData?.location),
      issueType: formData?.issueType,
      issueTypeLabel: toLabel(issueTypeOptions, formData?.issueType),
      startedAt: formData?.startTime || null,
      hazards: formData?.hazards || [],
      detailedDescription: formData?.detailedDescription || null
    },
    property: {
      propertyType: formData?.propertyType || null,
      propertyTypeLabel: toLabel(propertyOptions, formData?.propertyType),
      preferredTime: formData?.preferredTime || null
    },
    media: {
      photoCount: photoFiles?.length,
      hasVoiceNote: Boolean(voiceFile)
    }
  });

  const getRecommendedAction = (urgency) => {
    if (urgency === 'emergency') {
      return {
        title: 'Emergency response recommended',
        message: 'Call now and isolate water supply if safe. Your request will also be sent with all diagnostics and media.',
        badge: 'critical'
      };
    }

    if (urgency === 'urgent') {
      return {
        title: 'Same-day dispatch recommended',
        message: 'Submit now to allow pre-diagnosis before arrival and reduce on-site troubleshooting time.',
        badge: 'high'
      };
    }

    return {
      title: 'Detailed service assessment',
      message: 'Submit full details so the admin can prepare the right tools, parts, and technician.',
      badge: 'medium'
    };
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    setSubmitError('');

    const nextErrors = validate();
    if (Object.keys(nextErrors)?.length > 0) {
      setErrors(nextErrors);
      return;
    }

    setErrors({});
    setIsSubmitting(true);

    try {
      const diagnosticPayload = buildDiagnosticPayload();

      const { data, error } = await serviceRequestService?.create({
        name: formData?.name,
        phone: formData?.phone,
        email: formData?.email || null,
        serviceType: 'plumbing',
        urgency: formData?.urgency,
        preferredTime: formData?.preferredTime || null,
        diagnosticPayload,
        mediaFiles: {
          photos: photoFiles,
          voice: voiceFile
        }
      });

      if (error) {
        throw error;
      }

      setSubmitSuccess({
        requestId: data?.id,
        recommendation: getRecommendedAction(formData?.urgency)
      });

      setFormData(makeDefaultForm());
      clearPhotos();
      clearVoiceNote();
    } catch (error) {
      setSubmitError(error?.message || 'Failed to submit diagnostics. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    const recommendation = submitSuccess?.recommendation;

    return (
      <section className="py-12 md:py-16 lg:py-20 bg-muted/30">
        <div className="max-w-4xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="bg-card rounded-2xl p-8 md:p-12 border border-border shadow-lg">
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-success/10 border border-success text-success px-4 py-2 rounded-full mb-4">
                <Icon name="CheckCircle" size={18} />
                <span className="font-semibold text-sm">Diagnostic Request Submitted</span>
              </div>
              <h3 className="font-headline text-2xl md:text-3xl font-bold text-foreground mb-3">
                Your service request has been sent
              </h3>
              <p className="text-muted-foreground">
                Request ID: <span className="font-semibold text-foreground">{submitSuccess?.requestId || 'Pending'}</span>
              </p>
            </div>

            <div className="bg-accent/10 border border-accent/20 rounded-xl p-5 mb-8">
              <div className="flex items-start gap-3">
                <Icon name={recommendation?.badge === 'critical' ? 'AlertTriangle' : 'ClipboardCheck'} size={20} color="var(--color-accent)" />
                <div>
                  <h4 className="font-semibold text-foreground mb-1">{recommendation?.title}</h4>
                  <p className="text-sm text-muted-foreground">{recommendation?.message}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="default"
                size="lg"
                className="flex-1 bg-accent hover:bg-accent/90 text-accent-foreground emergency-cta"
                iconName="Phone"
                iconPosition="left"
                onClick={onEmergencyClick}
              >
                Call Now: 082 793 5785
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="RotateCcw"
                iconPosition="left"
                onClick={() => setSubmitSuccess(null)}
              >
                Submit Another Request
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-muted/30">
      <div className="max-w-5xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
            <Icon name="Stethoscope" size={20} color="var(--color-primary)" />
            <span className="text-sm font-semibold text-primary">Plumbing Diagnostic Intake</span>
          </div>
          <h2 className="font-headline text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            Help Us Diagnose Your Plumbing Issue Fast
          </h2>
          <p className="font-body text-lg text-muted-foreground max-w-3xl mx-auto">
            Submit detailed symptoms, photos, and a voice note so our admin can pre-diagnose the issue before dispatch.
          </p>
        </div>

        <div className="bg-card rounded-2xl p-6 md:p-10 border border-border shadow-lg">
          {submitError && (
            <div className="bg-error/10 border border-error text-error rounded-lg p-3 mb-6 text-sm">
              {submitError}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            <div>
              <h3 className="font-headline text-xl font-bold text-foreground mb-4">1. Contact Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Full Name"
                  name="name"
                  value={formData?.name}
                  onChange={handleFieldChange}
                  error={errors?.name}
                  required
                />
                <Input
                  label="Phone Number"
                  name="phone"
                  value={formData?.phone}
                  onChange={handleFieldChange}
                  error={errors?.phone}
                  required
                  placeholder="082 123 4567"
                />
                <Input
                  label="Email (Optional)"
                  name="email"
                  type="email"
                  value={formData?.email}
                  onChange={handleFieldChange}
                  error={errors?.email}
                  placeholder="you@example.com"
                />
                <Select
                  label="Preferred Callback Time"
                  options={preferredTimeOptions}
                  value={formData?.preferredTime}
                  onChange={(value) => handleSelectChange('preferredTime', value)}
                  placeholder="Select preferred time"
                />
              </div>
            </div>

            <div>
              <h3 className="font-headline text-xl font-bold text-foreground mb-4">2. Problem Overview</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Urgency"
                  options={severityOptions}
                  value={formData?.urgency}
                  onChange={(value) => handleSelectChange('urgency', value)}
                  error={errors?.urgency}
                  required
                />
                <Select
                  label="Problem Location"
                  options={locationOptions}
                  value={formData?.location}
                  onChange={(value) => handleSelectChange('location', value)}
                  error={errors?.location}
                  required
                />
                <Select
                  label="Issue Type"
                  options={issueTypeOptions}
                  value={formData?.issueType}
                  onChange={(value) => handleSelectChange('issueType', value)}
                  error={errors?.issueType}
                  required
                />
                <Select
                  label="Property Type"
                  options={propertyOptions}
                  value={formData?.propertyType}
                  onChange={(value) => handleSelectChange('propertyType', value)}
                  placeholder="Select property type"
                />
                <Input
                  label="When did it start?"
                  type="datetime-local"
                  name="startTime"
                  value={formData?.startTime}
                  onChange={handleFieldChange}
                />
                <Input
                  label="Is it recurring?"
                  name="recurringPattern"
                  value={formData?.recurringPattern}
                  onChange={handleFieldChange}
                  placeholder="e.g., every evening, after shower, random"
                />
              </div>
            </div>

            <div>
              <h3 className="font-headline text-xl font-bold text-foreground mb-4">3. Quick Notes (Optional)</h3>
              <div className="space-y-4">
                <div>
                  <p className="block font-body font-medium text-sm md:text-base text-foreground mb-2">
                    Safety hazards present
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {hazardOptions?.map((hazard) => {
                      const selected = formData?.hazards?.includes(hazard?.id);
                      return (
                        <label
                          key={hazard?.id}
                          className={`flex items-center gap-3 border rounded-lg px-3 py-2 text-sm transition-colors cursor-pointer ${selected ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background text-foreground hover:border-primary/40'}`}
                        >
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleHazard(hazard?.id)}
                            className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          />
                          <span>{hazard?.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <label className="block font-body font-medium text-sm md:text-base text-foreground mb-2">
                    Short description (optional)
                  </label>
                  <textarea
                    name="detailedDescription"
                    value={formData?.detailedDescription}
                    onChange={handleFieldChange}
                    rows={3}
                    className="w-full px-4 py-3 bg-background border border-input rounded-lg font-body text-sm md:text-base text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all resize-none"
                    placeholder="One or two lines about the problem..."
                  />
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-headline text-xl font-bold text-foreground mb-4">4. Media Evidence</h3>
              <div className="space-y-6">
                <div>
                  <label className="block font-body font-medium text-sm md:text-base text-foreground mb-2">
                    Upload photos (up to 5)
                  </label>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      iconName="Camera"
                      iconPosition="left"
                      onClick={startCameraCapture}
                    >
                      Use Device Camera
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      iconName="Camera"
                      iconPosition="left"
                      onClick={() => cameraInputRef?.current?.click()}
                    >
                      Take Photo (Browser)
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      iconName="Upload"
                      iconPosition="left"
                      onClick={() => uploadInputRef?.current?.click()}
                    >
                      Upload Photos
                    </Button>
                  </div>
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                  <input
                    ref={uploadInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoChange}
                    className="hidden"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    You can take a live photo from your camera or upload existing images.
                  </p>
                  {cameraError && (
                    <p className="text-xs text-error mt-2">{cameraError}</p>
                  )}
                  {showCameraCapture && (
                    <div className="mt-4 border border-border rounded-lg p-3 bg-muted/40">
                      <video
                        ref={cameraVideoRef}
                        className="w-full max-h-72 rounded-md bg-black object-cover"
                        playsInline
                        muted
                      />
                      <div className="flex gap-2 mt-3">
                        <Button
                          type="button"
                          variant="default"
                          size="sm"
                          iconName="Camera"
                          iconPosition="left"
                          onClick={capturePhotoFromCamera}
                        >
                          Capture Photo
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          iconName="X"
                          iconPosition="left"
                          onClick={stopCameraCapture}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                  {photoPreviews?.length > 0 && (
                    <div className="mt-4">
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {photoPreviews?.map((previewUrl, index) => (
                          <div key={previewUrl} className="aspect-square rounded-lg overflow-hidden border border-border bg-muted">
                            <img
                              src={previewUrl}
                              alt={`Issue preview ${index + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-3"
                        onClick={clearPhotos}
                        iconName="Trash2"
                        iconPosition="left"
                      >
                        Remove Photos
                      </Button>
                    </div>
                  )}
                </div>

                <div>
                  <p className="block font-body font-medium text-sm md:text-base text-foreground mb-2">
                    Voice description
                  </p>
                  <div className="flex flex-wrap gap-3 mb-3">
                    {recordingSupported && (
                      <Button
                        type="button"
                        variant={isRecording ? 'danger' : 'default'}
                        onClick={isRecording ? stopVoiceRecording : startVoiceRecording}
                        iconName={isRecording ? 'Square' : 'Mic'}
                        iconPosition="left"
                      >
                        {isRecording ? 'Stop Recording' : 'Record Voice Note'}
                      </Button>
                    )}

                    <label className="inline-flex items-center px-4 py-2 rounded-md border border-input cursor-pointer hover:bg-accent hover:text-accent-foreground text-sm">
                      <Icon name="Upload" size={16} className="mr-2" />
                      Upload Audio File
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={handleVoiceUpload}
                        className="hidden"
                      />
                    </label>

                    {voiceFile && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={clearVoiceNote}
                        iconName="Trash2"
                        iconPosition="left"
                      >
                        Remove Voice Note
                      </Button>
                    )}
                  </div>

                  {voicePreviewUrl && (
                    <div className="bg-muted/50 border border-border rounded-lg p-3">
                      <p className="text-sm font-medium mb-2">Voice note ready: {voiceFile?.name}</p>
                      <audio controls src={voicePreviewUrl} className="w-full" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 pt-2">
              <Button
                type="submit"
                variant="default"
                size="lg"
                loading={isSubmitting}
                className="w-full sm:flex-1"
                iconName="Send"
                iconPosition="left"
              >
                {isSubmitting ? 'Submitting Diagnostic Request...' : 'Submit for Admin Diagnosis'}
              </Button>

              <Button
                type="button"
                variant="outline"
                size="lg"
                iconName="Phone"
                iconPosition="left"
                onClick={onEmergencyClick}
              >
                Call Emergency Line
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default ProblemDiagnosisTool;
